import { Doc } from '@dadohr/shared/domainTypes/document';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { PAEventType } from '@dadohr/shared/domainTypes/productAnalytics';
import { PaletteColor, ThemeOverrides } from '@dadohr/shared/domainTypes/theme';
import { IMPORT_DATE_FORMATS } from '@dadohr/shared/domainTypes/time';
import { FS } from '@dadohr/shared/versions';
import { Theme, useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  capitalize,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import { ImageIcon, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import shortid from 'shortid';
import { TextFieldColorForm } from '../../../../components/ColorPicker';
import { CustomInputLabel } from '../../../../components/CustomInputLabel';
import { ImageUploadDialog } from '../../../../components/ImageUpload';
import { NoAccess } from '../../../../components/NoAccess';
import { useDialogState } from '../../../../hooks/useDialogState';
import { useTranslation, useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer, FlexContainerVertical } from '../../../../layout/Flex';
import { GridHalfSized } from '../../../../layout/Grid';
import { PageBody } from '../../../../layout/PageBody';
import { PageToolbar, PageToolbarTitle } from '../../../../layout/PageToolbar';
import { Section } from '../../../../layout/Section';
import { useRoutes } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { storage, updateDocFromPartial } from '../../../../services/firebase';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import { useProductAnalytics } from '../../../../services/productAnalytics';
import { useOrgSideNav } from '../sideNav';

const TYPES_TO_EXT: { [type: string]: string } = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/svg+xml': 'svg'
};

const updateName = async (orgId: string, name: string) => {
  const p: Doc<Pick<IOrganization, 'name'>> = {
    id: orgId,
    collection: FS.orgs,
    data: { name }
  };
  await updateDocFromPartial(p);
};

const updateDateFormats = async (
  orgId: string,
  dateFormats: IOrganization['dateFormats']
) => {
  const p: Doc<Pick<IOrganization, 'dateFormats'>> = {
    id: orgId,
    collection: FS.orgs,
    data: { dateFormats }
  };
  await updateDocFromPartial(p);
};

export const updateLogo = async (orgId: string, logoUrl: string) => {
  const p: Doc<Pick<IOrganization, 'logoUrl'>> = {
    id: orgId,
    collection: FS.orgs,
    data: { logoUrl }
  };
  await updateDocFromPartial(p);
};

export const updatePrimaryColors = (
  theming: ThemeOverrides | null,
  defaultTheme: Theme,
  primaryColors: Partial<PaletteColor>
): ThemeOverrides => {
  return {
    ...(theming || {}),
    colors: {
      ...(theming?.colors || {}),
      primary: {
        ...defaultTheme.custom.colors.primary,
        ...(theming?.colors?.primary || {}),
        ...primaryColors
      }
    }
  };
};

export const updateHeaderBackgroundDark = (
  theming: ThemeOverrides | null,
  defaultTheme: Theme,
  headerBackgroundDark: { main: string }
) => {
  return {
    ...(theming || {}),
    colors: {
      ...(theming?.colors || {}),
      headerBackgroundDark: {
        ...defaultTheme.custom.colors.headerBackgroundDark,
        ...(theming?.colors?.headerBackgroundDark || {}),
        ...headerBackgroundDark
      }
    }
  };
};

export const saveExperienceTheming = async (
  orgId: string,
  theming: ThemeOverrides
) => {
  const p: Doc<Pick<IOrganization, 'experienceTheming'>> = {
    id: orgId,
    collection: FS.orgs,
    data: { experienceTheming: theming }
  };
  await updateDocFromPartial(p);
};

export const updateInvertedLogoAndSave = async (
  orgId: string,
  logoInvertedUrl: string
) => {
  const p: Doc<Pick<IOrganization, 'logoInvertedUrl'>> = {
    id: orgId,
    collection: FS.orgs,
    data: { logoInvertedUrl }
  };
  await updateDocFromPartial(p);
};

export const storeImage = async (blob: Blob) => {
  const ext = TYPES_TO_EXT[blob.type];
  if (!ext) {
    throw new Error('UNKNOWN_TYPE');
  }

  const filePath = `org-logos/${shortid()}.${ext}`;
  const ref = storage().ref(filePath);
  await ref.put(blob);
  return await ref.getDownloadURL();
};

export const uploadLogoAndUseIt = async (org: IOrganization, blob: Blob) => {
  const downloadUrl = await storeImage(blob);
  await updateLogo(org.id, downloadUrl);
};

const NameEdit = ({ d }: { d: IOrganization }) => {
  const { t } = useTranslation();
  const originalName = d.name;
  const [value, setValue] = useState(originalName);
  useEffect(() => setValue(originalName), [originalName]);
  return (
    <form
      style={{ flex: 1 }}
      onSubmit={async (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (value !== d.name) {
          await updateName(d.id, value);
        }
      }}
    >
      <CustomInputLabel
        label={t('org.branding.orgName')}
        subLabel={capitalize(t('visible to employees'))}
      >
        <TextField
          value={value}
          onChange={(ev) => setValue(ev.target.value)}
          variant="outlined"
          fullWidth
        />
        {value !== d.name && (
          <Button variant="contained" color="primary" type="submit">
            {t('Update')}
          </Button>
        )}
      </CustomInputLabel>
    </form>
  );
};

const DefaultDateFormatEdit = ({ d }: { d: IOrganization }) => {
  const { t } = useUnsafeTranslation();
  const longFormat = d.dateFormats.MMDDYYYY || 'MM-DD-YYYY';
  const [value, setValue] = useState(longFormat);
  useEffect(() => setValue(longFormat), [longFormat]);
  return (
    <form
      style={{ flex: 1 }}
      onSubmit={async (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        if (value !== longFormat) {
          await updateDateFormats(d.id, {
            ...d.dateFormats,
            MMDDYYYY: value
          });
        }
      }}
    >
      <CustomInputLabel
        label={t('Default Date Format')}
        subLabel={capitalize(t('visible to employees'))}
        info={t(
          'Default date format to use when formatting dates in comms to your employees, as well as the required date format in Google Sheet Import Sources'
        )}
      >
        <Select
          value={value}
          onChange={(ev) => setValue(ev.target.value as string)}
          fullWidth
        >
          {IMPORT_DATE_FORMATS.filter((f) => f.value.includes('YYYY')).map(
            (f) => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
              </MenuItem>
            )
          )}
        </Select>
        {value !== longFormat && (
          <Button variant="contained" color="primary" type="submit">
            {t('Update')}
          </Button>
        )}
      </CustomInputLabel>
    </form>
  );
};

const ColoredContainer = styled('div')<{
  backgroundColor: string;
}>((p) => ({
  backgroundColor: p.backgroundColor,
  padding: p.theme.spacing(2)
}));

const RemoveButton = ({ onClick }: { onClick: () => void }) => {
  const { t } = useUnsafeTranslation();
  return (
    <Button color="secondary" variant="text" onClick={onClick} size="small">
      {t('Remove')}
    </Button>
  );
};

const ImageFallbackContainer = styled('div')<{
  height: number;
  backgroundColor: string;
}>((p) => ({
  backgroundColor: p.backgroundColor,
  height: p.height,
  width: 400,
  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius(),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  color: p.theme.custom.colors.gray.w500
}));

export const ImageUrl = ({
  value,
  onChange,
  defaultValue,
  backgroundColor,
  renderFallbackOnEmpty
}: {
  value: string;
  onChange: (nextValue: string) => void;
  defaultValue: string;
  backgroundColor: string;
  renderFallbackOnEmpty?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  const v = value || defaultValue;
  const imageHeight = 8 * 10;
  return (
    <>
      <FlexContainerVertical spacing={1} fullWidth>
        {v && (
          <ColoredContainer backgroundColor={backgroundColor}>
            <img src={v} height={imageHeight} alt="" />
          </ColoredContainer>
        )}
        {!v && renderFallbackOnEmpty && (
          <ImageFallbackContainer
            role="button"
            onClick={openDialog}
            backgroundColor={backgroundColor}
            height={imageHeight}
          >
            <ImageIcon size={20} />
          </ImageFallbackContainer>
        )}
        <FlexContainer alignItems="center">
          <Button
            startIcon={<Upload />}
            variant="outlined"
            onClick={openDialog}
          >
            {t('Upload logo')}
          </Button>
          {value && <RemoveButton onClick={() => onChange('')} />}
        </FlexContainer>
      </FlexContainerVertical>
      <ImageUploadDialog
        title={t('Upload logo image')}
        open={dialogOpen}
        onCancel={closeDialog}
        src={value}
        onUpload={async (blob) => {
          const url = await storeImage(blob);
          onChange(url);
        }}
        helperText={
          <Typography variant="caption">
            {t('Plese use a PNG, SVG or JPG file.')}
          </Typography>
        }
      />
    </>
  );
};

const ContentOrgBranding = () => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const sideNav = useOrgSideNav();
  const theme = useTheme();

  const { trackEvent } = useProductAnalytics();
  useEffect(() => {
    trackEvent({
      eventType: PAEventType.OTHER,
      name: 'General Settings Page Loaded',
      Explanation:
        'Admin user loads the general settings page, where logo, date format etc are set'
    });
  }, [trackEvent]);

  const primaryColors =
    org.experienceTheming?.colors?.primary || theme.custom.colors.primary;
  const headerBackgroundDarkColors = org.experienceTheming?.colors
    ?.headerBackgroundDark || { main: primaryColors.main };

  return (
    <PageBody noTopPadding sideNav={sideNav}>
      <PageToolbar>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('pageTitle.orgBranding')}
          </Typography>
        </PageToolbarTitle>
      </PageToolbar>

      <GridHalfSized>
        <div>
          <Section spacing={4}>
            <NameEdit d={org} />
          </Section>
          <Section spacing={4}>
            <DefaultDateFormatEdit d={org} />
          </Section>
          <Section spacing={4}>
            <FlexContainerVertical spacing={4} fullWidth>
              <Typography variant="h4">{t('Branding')}</Typography>
              <CustomInputLabel
                label={t('Logo for email notifications')}
                subLabel={t('Shown on a white background')}
              >
                <ImageUrl
                  value={org.logoUrl}
                  onChange={(nextUrl) => {
                    updateLogo(org.id, nextUrl);
                  }}
                  defaultValue={org.logoUrl}
                  backgroundColor={'#ffffff'}
                  renderFallbackOnEmpty
                />
              </CustomInputLabel>

              <CustomInputLabel
                label={t('Logo for Experience Hub header')}
                subLabel={t(
                  'Shown on a colored background, specified as header background color below'
                )}
              >
                <ImageUrl
                  value={org.logoInvertedUrl || org.logoUrl}
                  backgroundColor={headerBackgroundDarkColors.main}
                  onChange={(nextUrl) => {
                    updateInvertedLogoAndSave(org.id, nextUrl);
                  }}
                  defaultValue={org.logoInvertedUrl || org.logoUrl}
                  renderFallbackOnEmpty
                />
              </CustomInputLabel>

              <CustomInputLabel
                label={t('Header background color')}
                subLabel={t('Used for header banner on Experience Hub')}
              >
                <TextFieldColorForm
                  value={headerBackgroundDarkColors.main}
                  onSave={(nextColor) =>
                    saveExperienceTheming(
                      org.id,
                      updateHeaderBackgroundDark(org.experienceTheming, theme, {
                        main: nextColor
                      })
                    )
                  }
                />
              </CustomInputLabel>
              <CustomInputLabel
                label={t('Main color')}
                subLabel={t(
                  'Used for buttons and other highlights on Experience Hub and in emails'
                )}
              >
                <TextFieldColorForm
                  value={primaryColors.main}
                  onSave={(nextColor) =>
                    saveExperienceTheming(
                      org.id,
                      updatePrimaryColors(org.experienceTheming, theme, {
                        main: nextColor
                      })
                    )
                  }
                />
              </CustomInputLabel>
              <CustomInputLabel
                label={t('Darker color')}
                subLabel={t(
                  'A darker shade of the main color, used for hover states'
                )}
              >
                <TextFieldColorForm
                  value={primaryColors.dark}
                  onSave={(nextColor) =>
                    saveExperienceTheming(
                      org.id,
                      updatePrimaryColors(org.experienceTheming, theme, {
                        dark: nextColor
                      })
                    )
                  }
                />
              </CustomInputLabel>
              <CustomInputLabel
                label={t('Lighter color')}
                subLabel={t(
                  'A lighter shade of the main color, used for hover states'
                )}
              >
                <TextFieldColorForm
                  value={primaryColors.light}
                  onSave={(nextColor) =>
                    saveExperienceTheming(
                      org.id,
                      updatePrimaryColors(org.experienceTheming, theme, {
                        light: nextColor
                      })
                    )
                  }
                />
              </CustomInputLabel>

              <CustomInputLabel
                label={t('Text color')}
                subLabel={t(
                  'When text appears on a background that is the main color set above'
                )}
              >
                <TextFieldColorForm
                  value={primaryColors.contrastText}
                  onSave={(nextColor) =>
                    saveExperienceTheming(
                      org.id,
                      updatePrimaryColors(org.experienceTheming, theme, {
                        contrastText: nextColor
                      })
                    )
                  }
                />
              </CustomInputLabel>
            </FlexContainerVertical>
          </Section>
        </div>
        <div />
      </GridHalfSized>
    </PageBody>
  );
};

export const PageOrgBranding = () => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.org.branding)) {
    return <NoAccess />;
  }

  return <ContentOrgBranding />;
};
